import type { Load } from '../types';
import { dims, money, shortDate, weight } from '../lib/format';

function Field({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="field">
      <div className="label">{label}</div>
      <div className={`value${mono ? ' mono' : ''}`}>{value}</div>
    </div>
  );
}

export function LoadDetail({ load }: { load: Load }) {
  const dimDominant = load.estimatedDimWeight > load.totalWeight;

  return (
    <div className="panel">
      <div className="panel-head">
        <div>
          <h2>
            {load.loadId} · {load.custName}
          </h2>
          <div className="hint">
            Sales order {load.salesId} · {load.poNumber} · account {load.custAccount}
          </div>
        </div>
        <div className="posture">
          {load.residential && (
            <span className="chip">
              <span className="dot" aria-hidden="true" />
              Residential
            </span>
          )}
          <span className="chip">Class {load.freightClass}</span>
          <span className={`chip${load.daysToDeadline <= 1 ? ' critical' : ''}`}>
            {load.daysToDeadline}d of float
          </span>
        </div>
      </div>

      <div className="panel-body">
        <div className="detail-grid">
          <Field
            label="Ship from"
            value={
              <>
                {load.warehouse.inventLocationId} · {load.warehouse.city}, {load.warehouse.state}{' '}
                {load.warehouse.zip}
              </>
            }
          />
          <Field
            label="Ship to"
            value={
              <>
                {load.shipTo.address1}
                <br />
                {load.shipTo.city}, {load.shipTo.state} {load.shipTo.zip}
              </>
            }
          />
          <Field label="Ship date" value={shortDate(load.shipDate)} />
          <Field label="Required by" value={shortDate(load.requiredByDate)} />
          <Field
            label="Weight"
            value={
              <>
                {weight(load.totalWeight)}
                {dimDominant && (
                  <span className="sub-note">
                    {' '}
                    · cube implies ~{weight(load.estimatedDimWeight)}
                  </span>
                )}
              </>
            }
          />
          <Field label="Package" value={dims(load.package)} />
          <Field
            label="Handling unit"
            value={`${dims(load.pallet)} · ${load.pallet.pallets} plt / ${load.pallet.pieces} pc`}
          />
          <Field label="Declared value" value={money(load.declaredValue)} />
        </div>

        {load.lines.length > 0 && (
          <div style={{ marginTop: 14 }}>
            <div className="label" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', marginBottom: 4 }}>
              Lines
            </div>
            {load.lines.map((line) => (
              <div className="charge-row" key={line.itemId}>
                <span>
                  <span className="svc">
                    <span className="code">{line.itemId}</span>
                  </span>
                  {line.itemName} · {line.qty} {line.unit}
                </span>
                <span>{money(line.qty * line.unitValue)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
